import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Modal,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { Button } from 'src/components/shared/Button'
import { SearchMinor, CircleCancelMinor } from '@shopify/polaris-icons'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { RejectionReason } from 'src/interfaces/rejectionReason'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import _ from 'lodash'
import { GSIcon } from 'src/components/shared/GSIcon'
import { FetchRejectionReasonsToDecline } from 'src/constants/actionTypes'
import { getRejectionReasonsToDecline } from 'src/selectors/rejectionReason'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { ROUTES } from 'src/constants/routes'
import { useLocation, useNavigate } from 'react-router-dom'

interface Props {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: RejectionReason) => void | Promise<void>
  isLoading?: boolean
}

const DeclineReasonModalComponent: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { hasPermission } = useCheckPermissions()

  const reasons = useSelector(getRejectionReasonsToDecline)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchValue, setSearchValue] = useState<string>('')

  const [selectedReasonId, setSelectedReasonId] = useState<string>('')
  const [isFetchingReasons, setIsFetchingReasons] = useState(false)

  const searchDebounce = useRef(_.debounce(setSearchQuery, 300))

  useEffect(() => {
    searchDebounce.current(searchValue)
  }, [searchValue])

  const handleManageDeclineReasons = useCallback(() => {
    navigate(ROUTES.SETTINGS_DECLINE_REASONS_URL, { state: { background: location } })
  }, [navigate, location])

  const handleFetchReasons = useCallback(async () => {
    setIsFetchingReasons(true)
    try {
      await createAsyncAction<RejectionReason[], void>(
        dispatch,
        FetchRejectionReasonsToDecline.request(),
      )
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsFetchingReasons(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (isOpen) {
      handleFetchReasons()
      setSearchQuery('')
      setSearchValue('')
      setSelectedReasonId('')
    }
  }, [isOpen, handleFetchReasons])

  const filteredReasons = useMemo(() => {
    if (!searchQuery.trim()) {
      return reasons
    }

    const query = searchQuery.toLowerCase()

    return reasons.filter((reason) => reason.name.toLowerCase().includes(query))
  }, [reasons, searchQuery])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setSearchValue('')
  }, [])

  const handleReasonSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedReasonId(e.target.value)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (selectedReasonId) {
      const selectedReason = reasons.find((reason) => reason.id === selectedReasonId)

      if (selectedReason) {
        await onConfirm(selectedReason)
      }
    }
  }, [selectedReasonId, reasons, onConfirm])

  const handleClose = useCallback(() => {
    setSearchQuery('')
    setSearchValue('')
    setSelectedReasonId('')
    onClose()
  }, [onClose])

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="decline-reason-modal-title"
      aria-describedby="decline-reason-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4 h-full`}
    >
      <div
        data-testid="contractor-details-decline-reason-modal"
        css={tw`min-w-[450px] max-w-[450px] h-full flex flex-col rounded-lg overflow-hidden`}
        style={{ backgroundColor: colors.BASICS_WHITE }}
      >
        <ModalHeader
          title="Decline step"
          onClose={handleClose}
          closeButtonDataTestId="contractor-details-decline-reason-modal-close-btn"
        />
        <div css={tw`py-4 flex flex-col gap-4 flex-1 min-h-0`}>
          <div css={tw`px-4`}>
            {hasPermission(UserPermission.CustomerReviewRejectionReasonView) && (
              <Button
                size="small"
                color="grey"
                className={tw`w-full mb-4`}
                rightIcon={<GSIcon iconName="gs_pencil" />}
                onClick={handleManageDeclineReasons}
                data-testid="contractor-details-decline-reason-manage-reasons-btn"
              >
                Manage decline reasons
              </Button>
            )}
            <TextField
              placeholder="Search"
              type="text"
              variant="outlined"
              fullWidth
              value={searchValue}
              size="small"
              onChange={handleSearchChange}
              inputProps={{
                'data-testid': 'contractor-details-decline-reason-search-input',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchMinor width={20} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton css={tw`mr-[-12px] rounded-none`} onClick={handleClearSearch}>
                      <CircleCancelMinor width={20} fill="#B4AFC0" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  '&:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                    border: '1px solid var(--Shades-Grey-100, #EDEDED)',
                  },
                },
              }}
            />
          </div>

          <div css={tw`flex flex-col gap-2 flex-1 min-h-0`}>
            <Typography
              variant="body2"
              css={tw`font-semibold mx-4`}
              color={colors.BASICS_BLACK}
              sx={{ flexShrink: 0 }}
            >
              Select decline reason
            </Typography>
            {isFetchingReasons && (
              <Typography
                variant="body2"
                color={colors.SHADES_GREY_400}
                css={tw`py-4 flex-shrink-0`}
              >
                Loading reasons...
              </Typography>
            )}
            {!isFetchingReasons && filteredReasons.length === 0 && (
              <Typography
                variant="body2"
                color={colors.SHADES_GREY_400}
                css={tw`py-4 flex-shrink-0`}
              >
                {searchQuery
                  ? 'No reasons found matching your search.'
                  : 'No decline reasons available.'}
              </Typography>
            )}
            {!isFetchingReasons && filteredReasons.length > 0 && (
              <div css={tw`flex-1 min-h-0 overflow-y-auto`}>
                <RadioGroup value={selectedReasonId} onChange={handleReasonSelect}>
                  <div css={tw`flex flex-col gap-2`}>
                    {filteredReasons.map((reason) => {
                      const isSelected = selectedReasonId === reason.id

                      return (
                        <FormControlLabel
                          key={reason.id}
                          data-testid={`contractor-details-decline-reason-option-${reason.id}`}
                          value={reason.id}
                          control={
                            <Radio
                              sx={{
                                padding: 0,
                                color: colors.SHADES_GREY_200,
                                '&.Mui-checked': {
                                  color: colors.PRIMARY_PURPLE_500,
                                  '& .MuiSvgIcon-root': {
                                    fill: colors.PRIMARY_PURPLE_500,
                                  },
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              color={
                                isSelected ? colors.PRIMARY_PURPLE_500 : colors.SHADES_GREY_400
                              }
                            >
                              {reason.name}
                            </Typography>
                          }
                          sx={{ margin: 0, gap: '8px' }}
                          css={[
                            tw`mx-4`,
                            isSelected
                              ? tw`rounded-[8px] border border-solid py-2 px-3`
                              : tw`rounded-[8px] border border-solid py-2 px-3`,
                          ]}
                          style={{
                            borderColor: isSelected
                              ? colors.PRIMARY_PURPLE_500
                              : colors.SHADES_GREY_200,
                            backgroundColor: isSelected
                              ? colors.PRIMARY_PURPLE_25
                              : colors.BASICS_WHITE,
                          }}
                        />
                      )
                    })}
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>
        </div>

        <div
          css={tw`flex justify-between items-center px-6 py-4 border-0 border-t border-solid gap-4 flex-shrink-0`}
          style={{ borderColor: colors.SHADES_GREY_100 }}
        >
          <Button
            css={tw`max-w-[110px]`}
            disabled={isLoading}
            onClick={handleClose}
            color="grey"
            size="small"
            data-testid="contractor-details-decline-reason-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            css={tw`max-w-[110px]`}
            loading={isLoading}
            disabled={!selectedReasonId || isLoading}
            onClick={handleConfirm}
            color="red"
            size="small"
            data-testid="contractor-details-decline-reason-submit-btn"
          >
            Decline
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const DeclineReasonModal = memo(DeclineReasonModalComponent)
